import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Icons } from 'markcat-ui';
import { useRefreshBilling, useBillingStatus } from 'markcat-api-client';
import styles from './BillingReturn.module.css';

const POLL_INTERVAL_MS = 1500;
const MAX_WAIT_MS = 20000;

/**
 * Stripe redirect target after a successful checkout.
 * Polls /api/billing/status until the webhook lands (plan flips off "free" or
 * a stripe_subscription_id appears), then auto-forwards to /app/billing.
 * Falls back to a "still syncing" message + manual nav if the webhook is slow.
 */
export function BillingSuccess() {
  const refreshBilling = useRefreshBilling();
  const { data: status, refetch } = useBillingStatus();
  const navigate = useNavigate();
  const [syncing, setSyncing] = useState(true);
  const startedAtRef = useRef(Date.now());

  const synced =
    !!status &&
    (status.effective_plan !== 'free' || !!status.stripe_subscription_id);

  useEffect(() => {
    refreshBilling();
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try { await refetch(); } catch { /* keep polling */ }
    };
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    tick();

    const timeout = setTimeout(() => { setSyncing(false); }, MAX_WAIT_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (synced) navigate('/app/billing');
  }, [synced, navigate]);

  const waitedMs = Date.now() - startedAtRef.current;
  const stillSyncing = syncing && !synced;
  const timedOut = !syncing && !synced;

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={`${styles.icon} ${styles.iconSuccess}`} aria-hidden="true">
          <Icons.CheckIcon size={32} />
        </div>
        <h1 className={styles.title}>Welcome aboard</h1>
        <p className={styles.subtitle}>
          Your plan is live. The full fetch pipeline and your new classification
          credits are ready — go save something.
        </p>
        {stillSyncing && (
          <p className={styles.note}>
            Activating your plan… ({Math.ceil((MAX_WAIT_MS - waitedMs) / 1000)}s)
          </p>
        )}
        {timedOut && (
          <p className={styles.note}>
            Still syncing with Stripe. This sometimes takes a minute — refresh your
            billing page if you don't see the new plan yet.
          </p>
        )}
        <p className={styles.note}>A receipt is on its way from Stripe.</p>
        <div className={styles.actions}>
          <Link to="/app/billing" className={styles.ctaPrimary}>Go to billing</Link>
          <Link to="/app" className={styles.cta}>Back to app</Link>
        </div>
      </div>
    </div>
  );
}
